import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { hasArea, isReadOnly } from '../lib/permissions';
import { date, dateTime, money, percent, quantity, todayYmd } from '../lib/format';
import { toast } from '../lib/toast';
import { errorMessage } from '../lib/api-error';
import { VAT_TREATMENT_LABELS } from '../lib/contacts-api';
import {
  CHARGEABILITY_LABELS,
  CORRECTION_REASONS,
  CORRECTION_REASON_LABELS,
  DOCUMENT_LANGUAGE_LABELS,
  DOCUMENT_TYPE_LABELS,
  IC_LISTING_LABELS,
  IMMUTABLE_NOTICE,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_WARNINGS,
  POSITIVE_QUANTITY_PATTERN,
  PROFORMA_IMMUTABLE_NOTICE,
  STATUS_LABELS,
  STATUS_TONES,
  VAT_CATEGORY_LABELS,
  blockersOf,
  canIssueCreditNote,
  documentObservations,
  downloadInvoicePdf,
  formatStructuredCommunication,
  gridLabel,
  invoiceErrorMessage,
  mixedTreatmentsOf,
  toastInvoiceError,
  useCancelInvoice,
  useCreateCreditNote,
  useDeleteInvoice,
  useInvoice,
  useInvoicePayments,
  useIssueInvoice,
  useRecordPayment,
  useRemovePayment,
  type BlockerItem,
  type CorrectionReason,
  type CreateCreditNoteBody,
  type CreditNoteLineBody,
  type PaymentMethod,
  type SalesInvoice,
  type SalesInvoiceLine,
} from '../lib/invoices-api';
import { saveBlob, useSendInvoiceEmail } from '../lib/einvoice-api';
import { usePostDocument } from '../lib/accounting-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';

/**
 * Belge detayı — taslakta çalışma alanı, düzenlendikten sonra ARŞİV.
 *
 * İki ekran gibi davranır ve bu bilinçli:
 *   · TASLAK: düzenle / faturayı düzenle / iptal / sil.
 *   · DÜZENLENMİŞ: hiçbir yazma düğmesi yok, yerine dondurulmuş KDV kararı,
 *     kutular, zorunlu ibare metni, ödeme referansı ve dönem. Tek "yazma"
 *     eylemi KREDİ NOTUDUR.
 *
 * ⚠ Değişmezliği ekranda SÖYLEMEK zorundayız. Söylenmezse operatör faturayı
 * düzeltmek için doğrudan SQL'e uzanır; orada dondurma tetikleyicisi
 * (`sales_invoice_immutable`, ERRCODE P0001) onu anlaşılmaz bir hatayla
 * karşılar ve veri, düzeltilebilir olduğu tek noktayı çoktan geçmiş olur.
 */

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const readOnly = isReadOnly(user);

  const query = useInvoice(id);
  const invoice = query.data ?? null;

  /**
   * Düzenleme denemesinin hatası AYRI tutulur, toast'a bırakılmaz.
   *
   * ⚠ Bloke ediciler bir cümleye sığmaz: sunucu hepsini BİRDEN döndürür (tek
   * tek verilseydi operatör üç kez düzeltip üç kez denerdi) ve her biri ayrı
   * bir eylem ister — biri Ayarlar'a, biri cari kaydına, biri kalem
   * kataloğuna. Kaybolan bir toast bunu taşıyamaz.
   */
  const [issueError, setIssueError] = useState<unknown>(null);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  const issue = useIssueInvoice();
  const cancel = useCancelInvoice();
  const remove = useDeleteInvoice();

  if (query.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={24} />
      </div>
    );
  }
  if (query.isError || !invoice) {
    return (
      <Card>
        <ErrorState message={invoiceErrorMessage(query.error)} />
      </Card>
    );
  }

  const draft = invoice.status === 'DRAFT';
  const lines = invoice.lines ?? [];
  const blockers = blockersOf(issueError);
  const mixed = mixedTreatmentsOf(issueError);

  return (
    <>
      <PageHeader
        title={invoice.documentNumber ?? 'Numarasız taslak'}
        description={`${DOCUMENT_TYPE_LABELS[invoice.documentType]} · ${invoice.billingName}`}
        actions={
          <>
            {!draft && <PdfDownloadButton invoice={invoice} />}
            {/*
              ⚠⚠ TEK BAĞLANTI NOKTASI. `/einvoice`teki "Bekleyenler" listesi
              yalnız HİÇ hazırlanmamış belgeleri sayar (`einvoiceDocumentId IS
              NULL`); bir taşıma başladığı (hazırlandığı/reddedildiği/elle
              gönderildiği) an belge o listeden TAMAMEN düşer. Bu bağlantı
              olmadan operatör kendi kestiği bir e-faturanın durumunu bir daha
              hiç göremezdi — ne kadarı OKUMA yetkisidir, o yüzden `readOnly`a
              bakmıyor.
            */}
            {!draft && invoice.einvoiceRequired && hasArea(user, 'einvoice') && (
              <Button
                variant="secondary"
                onClick={() => void navigate(`/einvoice?invoice=${invoice.id}`)}
              >
                E-fatura durumu
              </Button>
            )}
            {!draft && !readOnly && <EmailSendButton invoice={invoice} />}
            {!readOnly && (
              <>
                {draft ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => void navigate(`/invoices/${invoice.id}/edit`)}
                    >
                      Taslağı düzenle
                    </Button>
                    <Button variant="secondary" onClick={() => setCancelOpen(true)}>
                      İptal et
                    </Button>
                    <Button
                      variant="danger"
                      loading={remove.isPending}
                      onClick={() => {
                        // ⚠ Yalnız TASLAK silinebilir: numara almış belge kesintisiz
                        // dizinin parçasıdır ve silinmesi dizide DELİK açar.
                        if (!window.confirm('Bu taslak kalıcı olarak silinsin mi?')) return;
                        remove.mutate(invoice.id, {
                          onSuccess: () => {
                            toast.success('Taslak silindi.');
                            void navigate('/invoices', { replace: true });
                          },
                          onError: toastInvoiceError,
                        });
                      }}
                    >
                      Sil
                    </Button>
                    <Button
                      loading={issue.isPending}
                      onClick={() => {
                        setIssueError(null);
                        setConfirmIssue(true);
                      }}
                    >
                      Faturayı düzenle
                    </Button>
                  </>
                ) : (
                  canIssueCreditNote(invoice) && (
                    <Button onClick={() => setCreditOpen(true)}>Kredi notu kes</Button>
                  )
                )}
              </>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONES[invoice.status]}>{STATUS_LABELS[invoice.status]}</Badge>
        <Badge tone="neutral">{DOCUMENT_TYPE_LABELS[invoice.documentType]}</Badge>
        {invoice.correctsDocumentNumber && (
          <Badge tone="warn">düzeltir: {invoice.correctsDocumentNumber}</Badge>
        )}
        {invoice.isHistoricalImport && <Badge tone="neutral">geçmişten aktarıldı</Badge>}
      </div>

      {blockers.length > 0 && <BlockerPanel blockers={blockers} invoice={invoice} />}
      {mixed.length > 0 && <MixedTreatmentPanel treatments={mixed} />}
      {issueError !== null && blockers.length === 0 && mixed.length === 0 && (
        <Card className="mb-4 border-[var(--c-danger)]">
          <CardHeader title="Belge düzenlenemedi" />
          <p className="px-4 py-4 text-sm text-[var(--c-danger)]">
            {invoiceErrorMessage(issueError)}
          </p>
        </Card>
      )}

      {!draft && <ImmutabilityNotice invoice={invoice} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <PartiesCard invoice={invoice} />
          <LinesCard lines={lines} />
          {!draft && <VatDecisionCard invoice={invoice} />}
          {(invoice.notes || invoice.internalNotes) && <NotesCard invoice={invoice} />}
        </div>

        <div className="space-y-4">
          <TotalsCard invoice={invoice} />
          <DatesCard invoice={invoice} />
          {!draft && <PaymentCard invoice={invoice} />}
          <ObservationsCard invoice={invoice} />
        </div>
      </div>

      {/* ── düzenleme onayı ─────────────────────────────────────────── */}
      <Modal
        open={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        title="Faturayı düzenle"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmIssue(false)}>
              Vazgeç
            </Button>
            <Button
              loading={issue.isPending}
              onClick={() => {
                issue.mutate(invoice.id, {
                  onSuccess: (saved) => {
                    setConfirmIssue(false);
                    setIssueError(null);
                    toast.success(`Belge düzenlendi: ${saved.documentNumber ?? '—'}`);
                  },
                  onError: (err) => {
                    setConfirmIssue(false);
                    setIssueError(err);
                  },
                });
              }}
            >
              Evet, düzenle
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-[var(--c-ink-soft)]">
          <p className="font-medium text-[var(--c-ink)]">Bu adım GERİ ALINAMAZ.</p>
          <p>
            Belge kesintisiz diziden bir numara alır, KDV kararı ve zorunlu ibareler DONAR, belge
            değişmez hâle gelir. Bundan sonra bir hata bulunursa düzeltmenin tek yolu kredi notudur
            (KB nr. 1 md. 12).
          </p>
          <p>
            Düzenlemeden önce müşteriyi, satırları ve tarihleri son kez kontrol edin: teslim tarihi
            belgenin hangi KDV dönemine düşeceğini belirler.
          </p>
        </div>
      </Modal>

      {/* ── taslak iptali ───────────────────────────────────────────── */}
      <CancelDraftModal
        open={cancelOpen}
        pending={cancel.isPending}
        onClose={() => setCancelOpen(false)}
        onSubmit={(reason) => {
          cancel.mutate(
            { id: invoice.id, reason },
            {
              onSuccess: () => {
                setCancelOpen(false);
                toast.success('Taslak iptal edildi.');
              },
              onError: toastInvoiceError,
            },
          );
        }}
      />

      {/* ── kredi notu ──────────────────────────────────────────────── */}
      {creditOpen && (
        <CreditNoteModal
          invoice={invoice}
          lines={lines}
          onClose={() => setCreditOpen(false)}
          onCreated={(created) => {
            setCreditOpen(false);
            toast.success('Kredi notu TASLAĞI açıldı. Numarası düzenlendiğinde verilecek.');
            void navigate(`/invoices/${created.id}`);
          }}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────── bloke edicilerin gösterimi

/**
 * `INVOICE_VAT_BLOCKED` panelinin TAMAMI.
 *
 * ⚠ Her bloke edici, operatörün NE YAPACAĞINI söyleyen bir cümleye ve
 * mümkünse DÜZELTMENİN ADRESİNE çevrilir. Kodu ham göstermek (ya da sunucunun
 * İngilizce `message` alanını basmak) operatörü "bir şeyler ters gitti"
 * noktasında bırakırdı; oysa bu kodların her biri somut ve farklı bir eylem
 * ister.
 */
/**
 * PDF — düzenlenmiş belgenin müşteriye gidecek hâli.
 *
 * ⚠ YALNIZ DÜZENLENMİŞ BELGEDE: taslağın numarası, KDV kararı ve zorunlu
 * ibaresi henüz donmamıştır (sunucu da `INVOICE_PDF_NOT_ISSUED` ile reddeder).
 * Salt okuma rolü de indirebilir — indirmek bir yazma değildir.
 */
function PdfDownloadButton({ invoice }: { invoice: SalesInvoice }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="secondary"
      loading={busy}
      onClick={() => {
        setBusy(true);
        void downloadInvoicePdf(invoice.id)
          .then((file) => {
            const fallback = `${(invoice.documentNumber ?? invoice.id).replace(/[^A-Za-z0-9._-]/g, '_')}.pdf`;
            saveBlob(file.blob, file.fileName ?? fallback);
          })
          .catch(toastInvoiceError)
          .finally(() => setBusy(false));
      }}
    >
      PDF indir
    </Button>
  );
}

/**
 * E-POSTAYLA GÖNDER — Peppol dışı kanal.
 *
 * ⚠⚠ BU "PDF YOLLAMAK" DEĞİLDİR: ekte UBL (faturanın kendisi) ve PDF birlikte
 * gider, ve kanal yalnız MÜŞTERİ ANLAŞTIYSA meşrudur. Rıza carinin kaydındadır;
 * yoksa sunucu reddeder ve ekran o cümleyi gösterir — panel burada kendi
 * kuralını kurmaz.
 *
 * ⚠ Onay adımı var: gönderim geri alınamaz, belge `SENT_MANUALLY` olur.
 */
function EmailSendButton({ invoice }: { invoice: SalesInvoice }) {
  const sendEmail = useSendInvoiceEmail();
  return (
    <Button
      variant="secondary"
      loading={sendEmail.isPending}
      onClick={() => {
        if (
          !window.confirm(
            'Fatura, müşterinin e-fatura adresine gönderilsin mi? Ekte UBL ve PDF gider; belge "gönderildi" olarak kapanır.',
          )
        ) {
          return;
        }
        sendEmail.mutate(invoice.id, {
          onSuccess: (res) => toast.success(`Gönderildi: ${res.recipient}`),
          onError: toastInvoiceError,
        });
      }}
    >
      E-postayla gönder
    </Button>
  );
}

function BlockerPanel({ blockers, invoice }: { blockers: BlockerItem[]; invoice: SalesInvoice }) {
  return (
    <Card className="mb-4 border-[var(--c-danger)]">
      <CardHeader
        title="Belge düzenlenemedi — bloke ediciler"
        description="Aşağıdakilerin HEPSİ giderilmeden numara verilmez. Sunucu bloke edicileri birleştirerek döndürür; teker teker düzeltip yeniden denemeniz gerekmez."
      />
      <ul className="divide-y divide-[var(--c-border)]">
        {blockers.map((blocker) => (
          <li key={blocker.code} className="px-4 py-3">
            <p className="font-mono text-[11px] text-[var(--c-ink-faint)]">{blocker.code}</p>
            <p className="mt-1 text-sm text-[var(--c-ink)]">{blocker.text}</p>

            {blocker.target === 'settings' && (
              <p className="mt-2 text-sm">
                <Link to="/settings" className="text-[var(--c-brand)] hover:underline">
                  Ayarlar sayfasını aç
                </Link>
                {blocker.settingKey && (
                  <span className="ml-2 font-mono text-[11px] text-[var(--c-ink-faint)]">
                    {blocker.settingKey}
                  </span>
                )}
              </p>
            )}

            {blocker.target === 'contact' && (
              <p className="mt-2 text-sm">
                <Link
                  to={`/contacts/${invoice.contactId}`}
                  className="text-[var(--c-brand)] hover:underline"
                >
                  {invoice.billingName} — cari detayına git
                </Link>
              </p>
            )}

            {blocker.target === 'lines' && (
              <p className="mt-2 text-sm">
                <Link
                  to={`/invoices/${invoice.id}/edit`}
                  className="text-[var(--c-brand)] hover:underline"
                >
                  Satırları düzenle
                </Link>
              </p>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * ⚠ TEK BELGE = TEK MUAMELE. Zorunlu ibare, IC listing kodu ve kutu eşleme
 * BELGE seviyesindedir; karışık bir belgede hangi ibarenin basılacağı ve
 * belgenin listing'e hangi kodla gireceği kararsızdır.
 */
function MixedTreatmentPanel({ treatments }: { treatments: string[] }) {
  return (
    <Card className="mb-4 border-[var(--c-danger)]">
      <CardHeader title="Belgede birden çok KDV muamelesi var" />
      <div className="space-y-3 px-4 py-4 text-sm text-[var(--c-ink-soft)]">
        <p>
          Bir belgede tek KDV muamelesi olabilir; zorunlu ibare ve kutu eşleme BELGE seviyesindedir.{' '}
          <strong>Doğru çözüm iki ayrı faturadır.</strong>
        </p>
        <p>
          Satırlar şu muamelelere düşüyor:{' '}
          {treatments
            .map((t) => VAT_TREATMENT_LABELS[t as keyof typeof VAT_TREATMENT_LABELS] ?? t)
            .join(' · ')}
        </p>
        <p className="text-xs">
          "İlkini seç" gibi bir kural, AB B2B ters yükümlülük satırıyla yurtiçi %21 satırını aynı
          belgeye koyardı: kutu 44 matrahı eksik, kutu 3 matrahı fazla çıkar ve ikisi de ancak
          denetimde görülür.
        </p>
      </div>
    </Card>
  );
}

function ImmutabilityNotice({ invoice }: { invoice: SalesInvoice }) {
  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start gap-3 px-4 py-3">
        <Badge tone="warn">değişmez</Badge>
        <p className="min-w-[16rem] flex-1 text-sm text-[var(--c-ink-soft)]">
          {invoice.documentType === 'PROFORMA' ? PROFORMA_IMMUTABLE_NOTICE : IMMUTABLE_NOTICE}
          {invoice.status === 'CANCELLED' && (
            <>
              {' '}
              Bu belge <strong>taslakken</strong> iptal edilmiş; hiç numara almadı ve hiçbir KDV
              dönemine düşmedi.
            </>
          )}
        </p>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────── kartlar

function PartiesCard({ invoice }: { invoice: SalesInvoice }) {
  return (
    <Card>
      <CardHeader
        title="Taraflar"
        description="Düzenleme anında DONDURULMUŞ anlık görüntü: cari ya da ayarlar sonradan değişse de belge değişmez."
      />
      <div className="grid gap-4 px-4 py-4 text-sm sm:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-semibold text-[var(--c-ink-soft)]">Alıcı</p>
          <Link
            to={`/contacts/${invoice.contactId}`}
            className="font-medium text-[var(--c-brand)] hover:underline"
          >
            {invoice.billingLegalName ?? invoice.billingName}
          </Link>
          <Field label="KDV numarası" value={invoice.billingVatNumber} mono />
          <Field label="İşletme numarası" value={invoice.billingEnterpriseNumber} mono />
          <Field
            label="Adres"
            value={[
              invoice.billingAddressLine1,
              invoice.billingAddressLine2,
              [invoice.billingPostalCode, invoice.billingCity].filter(Boolean).join(' '),
              invoice.billingCountry,
            ]
              .filter((part) => part && part.length > 0)
              .join(', ')}
          />
          <Field label="Müşteri tipi" value={invoice.billingCustomerType} />
          {/* ⚠ AB üyeliğinin DONDUĞU tek yer: üyelik tarih etkilidir (Brexit). */}
          <Field label="Teslim anında AB içi mi" value={invoice.billingIsEu ? 'evet' : 'hayır'} />
        </div>

        <div>
          <p className="mb-1 text-xs font-semibold text-[var(--c-ink-soft)]">Satıcı</p>
          <p className="font-medium">{invoice.sellerName}</p>
          <Field label="KDV numarası" value={invoice.sellerVatNumber} mono />
          <Field label="İşletme numarası" value={invoice.sellerEnterpriseNumber} mono />
          <Field label="Adres" value={invoice.sellerAddress} />
          <Field label="IBAN" value={invoice.sellerIban} mono />
          <Field
            label="Belgenin yasal dili"
            value={DOCUMENT_LANGUAGE_LABELS[invoice.documentLanguage]}
          />
        </div>
      </div>
    </Card>
  );
}

function LinesCard({ lines }: { lines: SalesInvoiceLine[] }) {
  return (
    <Card>
      <CardHeader
        title="Satırlar"
        description="Satır başına KDV KOLONU YOKTUR: KDV, (oran, kategori) grubunun toplam matrahı üzerinden bir kez hesaplanır (EN 16931 BR-CO-17)."
      />
      <Table>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>Açıklama</Th>
            <Th align="right">Adet</Th>
            <Th align="right">Birim fiyat</Th>
            <Th align="right">İndirim</Th>
            <Th align="right">KDV</Th>
            <Th align="right">Satır toplamı</Th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              <Td>{line.lineNo}</Td>
              <Td>
                {line.description}
                {line.servicePeriodStart && line.servicePeriodEnd && (
                  <span className="block text-xs text-[var(--c-ink-faint)]">
                    dönem: {date(line.servicePeriodStart)} – {date(line.servicePeriodEnd)}
                  </span>
                )}
                {line.vatExemptionReasonText && (
                  <span className="block text-xs text-[var(--c-ink-faint)]">
                    {line.vatExemptionReasonText}
                  </span>
                )}
              </Td>
              <Td align="right">{quantity(line.quantity)}</Td>
              <Td align="right">{money(line.unitPrice)}</Td>
              <Td align="right">
                {line.discountPct === '0.00' ? '—' : percent(line.discountPct)}
                {line.discountAmount !== '0.00' && (
                  <span className="block text-xs text-[var(--c-ink-faint)]">
                    {money(line.discountAmount)}
                  </span>
                )}
              </Td>
              <Td align="right">
                {percent(line.vatRate)}
                <span className="block text-xs text-[var(--c-ink-faint)]">
                  {VAT_CATEGORY_LABELS[line.vatCategory] ?? line.vatCategory}
                </span>
              </Td>
              <Td align="right">{money(line.lineTotalExclVat)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/**
 * Dondurulmuş KDV kararı: muamele, kutularıyla kırılım, zorunlu ibare metni.
 *
 * ⚠ KUTULAR DONDURULMUŞTUR, canlı türetilmez. Kredi notunun 48 mi 49 mu olduğu
 * ORİJİNAL faturanın kutusuna bağlıdır; canlı türetilseydi kural değiştiğinde
 * (ViDA 2027) ZATEN VERİLMİŞ bir çeyreğin kutuları geriye dönük değişirdi.
 */
function VatDecisionCard({ invoice }: { invoice: SalesInvoice }) {
  const mentions = invoice.vatLegalMention?.mentions ?? [];

  return (
    <Card>
      <CardHeader
        title="Dondurulmuş KDV kararı"
        description="Düzenleme anında verilmiş ve belgeye yazılmış karar. Kural sonradan değişse de bu belge değişmez."
      />

      <div className="space-y-4 px-4 py-4 text-sm">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field
            label="KDV muamelesi"
            value={VAT_TREATMENT_LABELS[invoice.vatTreatment] ?? invoice.vatTreatment}
          />
          <Field
            label="Muacceliyet esası"
            value={CHARGEABILITY_LABELS[invoice.chargeabilityBasis]}
          />
          <Field
            label="IC listing kodu"
            value={invoice.icListingCode ? IC_LISTING_LABELS[invoice.icListingCode] : null}
          />
          <Field label="VIES durumu" value={invoice.viesStatus} />
          <Field
            label="VIES kanıt numarası"
            value={invoice.viesRequestIdentifier}
            mono
            hint="VIES'in verdiği TEK yasal kanıt (qualified confirmation)."
          />
          <Field label="VIES sorgu anı" value={dateTime(invoice.viesCheckedAt)} />
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-[var(--c-ink-soft)]">
            KDV kırılımı ve Intervat kutuları
          </p>
          {invoice.vatBreakdown.length === 0 ? (
            <p className="text-xs text-[var(--c-warn)]">
              Kırılım boş. Düzenlenmiş bir belgede bu, belgenin KDV beyanına hiç girmemesi demektir
              — sunucu yöneticisine bildirin.
            </p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Oran</Th>
                  <Th>Kategori</Th>
                  <Th align="right">Matrah</Th>
                  <Th align="right">KDV</Th>
                  <Th>Kutular</Th>
                </tr>
              </thead>
              <tbody>
                {invoice.vatBreakdown.map((entry) => (
                  <tr key={`${entry.category}|${entry.rate}`}>
                    <Td>{percent(entry.rate)}</Td>
                    <Td>
                      {VAT_CATEGORY_LABELS[entry.category] ?? entry.category}
                      {entry.exemptionReasonCode && (
                        <span className="block font-mono text-[11px] text-[var(--c-ink-faint)]">
                          {entry.exemptionReasonCode}
                        </span>
                      )}
                    </Td>
                    <Td align="right">{money(entry.base)}</Td>
                    <Td align="right">{money(entry.vat)}</Td>
                    <Td>
                      <span className="block text-xs">matrah → {gridLabel(entry.grid)}</span>
                      <span className="block text-xs text-[var(--c-ink-faint)]">
                        {entry.taxGrid
                          ? `KDV → ${gridLabel(entry.taxGrid)}`
                          : 'KDV kutusu yok (ters yükümlülük / muafiyet / kapsam dışı)'}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-[var(--c-ink-soft)]">
            Zorunlu ibareler — belgeye BASILAN metin
          </p>
          {mentions.length === 0 ? (
            <p className="text-xs text-[var(--c-ink-faint)]">
              Bu muamele için özel bir ibare gerekmiyor (yurtiçi satışta olağandır); temel zorunlu
              içerik KB nr. 1 md. 5 §1 ile zaten karşılanır.
            </p>
          ) : (
            <ul className="space-y-2">
              {mentions.map((mention) => (
                <li
                  key={mention.code}
                  className="rounded-[var(--r-md)] bg-[var(--c-surface-alt)] px-3 py-2"
                >
                  <p className="font-medium">
                    {mention.text[invoice.documentLanguage] ?? '(metin yok)'}
                  </p>
                  <p className="mt-1 text-xs text-[var(--c-ink-faint)]">
                    {mention.legalBasis} · {mention.code}
                  </p>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-[var(--c-ink-faint)]">
            İbare metni serbest giriş değildir: CJEU C-247/21 birebir yazımı ister, eşanlamlısını
            kabul etmez ve sonradan eklenmesine izin vermez.
          </p>
        </div>
      </div>
    </Card>
  );
}

function TotalsCard({ invoice }: { invoice: SalesInvoice }) {
  return (
    <Card>
      <CardHeader title="Toplamlar" />
      <dl className="space-y-1 px-4 py-4 text-sm">
        <Row label="Matrah (BT-109)" value={money(invoice.subtotalExclVat)} />
        {invoice.discountAmount !== '0.00' && (
          <Row label="Belge indirimi" value={money(invoice.discountAmount)} />
        )}
        <Row label="KDV" value={money(invoice.vatAmount)} />
        {invoice.roundingAmount !== '0.00' && (
          <Row label="Yuvarlama (BT-114)" value={money(invoice.roundingAmount)} />
        )}
        <div className="border-t border-[var(--c-border)] pt-1">
          <Row label="Genel toplam" value={money(invoice.totalInclVat)} strong />
        </div>
        {invoice.amountPaid !== '0.00' && (
          <Row label="Tahsil edilen" value={money(invoice.amountPaid)} />
        )}
      </dl>
      {invoice.documentType === 'CREDIT_NOTE' && (
        <p className="border-t border-[var(--c-border)] px-4 py-3 text-xs text-[var(--c-ink-faint)]">
          ⚠ Kredi notunda tutarlar POZİTİF yazılır; işareti belge türü taşır (EN 16931 tip kodu 381,
          Intervat negatif tutarı şema seviyesinde kabul etmez).
        </p>
      )}
    </Card>
  );
}

function DatesCard({ invoice }: { invoice: SalesInvoice }) {
  return (
    <Card>
      <CardHeader title="Tarihler ve KDV dönemi" />
      <div className="px-4 py-4 text-sm">
        <Field label="Düzenleme" value={date(invoice.issueDate)} />
        <Field label="Teslim / ifa" value={date(invoice.supplyDate)} />
        {invoice.deliveryDate && <Field label="Sevk" value={date(invoice.deliveryDate)} />}
        <Field label="Vade" value={date(invoice.dueDate)} />
        {invoice.issuedAt && <Field label="Düzenlenme anı" value={dateTime(invoice.issuedAt)} />}

        {/*
         * ⚠ BELGE KDV DÖNEMİNE `vatChargeableAt` ÜZERİNDEN DÜŞER, `issueDate`
         * üzerinden DEĞİL. Kasa esasında (B2C, kamu kurumu) bu alan ancak
         * tahsilatta dolar; boş olması eksik beyan değildir.
         */}
        <Field
          label="KDV muaccel tarihi"
          value={invoice.vatChargeableAt ? date(invoice.vatChargeableAt) : null}
          hint={
            invoice.vatChargeableAt
              ? undefined
              : 'Henüz muaccel olmadı — kasa esasında tahsilatı bekliyor.'
          }
        />
        <Field
          label="KDV dönemi"
          value={invoice.vatPeriodKey}
          mono
          hint={invoice.vatPeriodKey ? undefined : 'Muacceliyet gerçekleşince atanır.'}
        />
        {invoice.retentionUntil && (
          <Field
            label="Yasal asgari saklama"
            value={date(invoice.retentionUntil)}
            hint="md. 60 §3 asgarisi. Saklama politikamız daha uzun olabilir."
          />
        )}
      </div>
    </Card>
  );
}

function PaymentCard({ invoice }: { invoice: SalesInvoice }) {
  const structured = formatStructuredCommunication(invoice.structuredCommunication);

  return (
    <Card>
      <CardHeader title="Ödeme" />
      <div className="px-4 py-4 text-sm">
        <Field label="IBAN" value={invoice.sellerIban} mono />
        {invoice.sellerBic && <Field label="BIC" value={invoice.sellerBic} mono />}
        {structured ? (
          <Field
            label="Yapılandırılmış mesaj"
            value={structured}
            mono
            hint="Ödeme bu referansla eşleştirilir; müşteri serbest açıklama yazmamalı."
          />
        ) : (
          <p className="mt-2 text-xs text-[var(--c-ink-faint)]">
            {invoice.documentType === 'INVOICE'
              ? 'Yapılandırılmış mesaj yalnız düzenlenmiş faturaya verilir.'
              : 'Bu belge ödeme talep etmez; yapılandırılmış mesaj verilmez.'}
          </p>
        )}

        <PaymentsSection invoice={invoice} />
      </div>
    </Card>
  );
}

/**
 * TAHSİLAT KAYDI VE GEÇMİŞİ.
 *
 * ⚠⚠ BU BÖLÜM UZUN SÜRE YOKTU ve eksikliği iki katmanlıydı. Görünen katman:
 * hiçbir fatura "ödendi" olamıyor, her belge sonsuza kadar gecikmiş görünüyordu.
 * Görünmeyen katman daha ağır: KASA ESASLI belgelerde (B2C, kamu kurumu) KDV
 * md. 22bis §3 uyarınca TAHSİLATTA muaccel olur — tahsilat kaydedilemediği
 * için o faturalar HİÇBİR KDV BEYANINA GİRMİYORDU. Ekran bunu "doğru davranış,
 * eksik beyan değil" diye açıklıyordu; doğruydu, ama yalnız tahsilat
 * kaydedilebildiği sürece.
 *
 * ⚠ NAKİT TAHSİLAT AYRI BİR YÖNTEMDİR: banka hareketi yoktur ve CODA
 * eşleştirmesiyle hiç bulunamaz. Elle kaydedilmesi şart.
 */
function PaymentsSection({ invoice }: { invoice: SalesInvoice }) {
  const { user } = useAuth();
  const payments = useInvoicePayments(invoice.id);
  const record = useRecordPayment(invoice.id);
  const remove = useRemovePayment(invoice.id);
  const post = usePostDocument();
  // Kayıt `accounting` alanının işidir; o alanı olmayana düğme gösterilmez.
  const canPost = hasArea(user, 'accounting') && !isReadOnly(user);
  // Kart / "diğer" tahsilatta para bir aracıya düşer; hesabı operatör yazar
  // (sunucu planla denetler). Banka ve kasada hesap yöntemden kesin türer.
  const [moneyAccount, setMoneyAccount] = useState<Record<string, string>>({});
  const needsAccount = (m: string) => m !== 'BANK' && m !== 'CASH';

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(todayYmd());
  const [method, setMethod] = useState<PaymentMethod>('BANK');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const kalan = Number(invoice.totalInclVat) - Number(invoice.amountPaid);

  return (
    <div className="mt-4 border-t border-[var(--c-border)] pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-[var(--c-ink-soft)]">Tahsilat</p>
          <p>
            {money(invoice.amountPaid)} / {money(invoice.totalInclVat)}
            {kalan > 0.004 && (
              <span className="text-[var(--c-ink-soft)]"> · kalan {money(String(kalan))}</span>
            )}
          </p>
        </div>
        {invoice.documentType !== 'PROFORMA' && (
          <Button
            variant={kalan > 0.004 ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setError(null);
              setWarnings([]);
              // ⚠ Alan kalan tutarla açılır: en sık yapılan işlem tam tahsilattır.
              setAmount(kalan > 0 ? kalan.toFixed(2) : '');
              setOpen((v) => !v);
            }}
          >
            {open ? 'Vazgeç' : 'Tahsilat ekle'}
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <Input
            label="Tutar"
            hint="Nokta ile: 121.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Input
            label="Tahsilat günü"
            type="date"
            hint="⚠ Kasa esaslı belgede KDV BU GÜN muaccel olur — kaydı girdiğiniz gün değil."
            value={receivedOn}
            onChange={(e) => setReceivedOn(e.target.value)}
          />
          <Select
            label="Yöntem"
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
          <Button
            disabled={record.isPending}
            onClick={() => {
              setError(null);
              setWarnings([]);
              record
                .mutateAsync({ amount, receivedOn, method })
                .then((r) => {
                  setWarnings(r.warnings);
                  setOpen(false);
                  toast.success('Tahsilat kaydedildi.');
                })
                .catch((e: unknown) => setError(errorMessage(e)));
            }}
          >
            Kaydet
          </Button>
          {error !== null && <p className="text-xs text-[var(--c-danger)]">{error}</p>}
        </div>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1">
          {warnings.map((w) => (
            <li key={w} className="text-xs text-[var(--c-warn)]">
              ⚠ {PAYMENT_WARNINGS[w] ?? w}
            </li>
          ))}
        </ul>
      )}

      {payments.isSuccess && payments.data.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs">
          {payments.data.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span>
                {date(p.receivedOn)} · {money(p.amount)} ·{' '}
                {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                {p.bankAllocationId !== null && (
                  <span className="text-[var(--c-ink-faint)]"> · banka eşleştirmesi</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                {p.journalEntryNumber ? (
                  <Badge tone="ok">defterde · {p.journalEntryNumber}</Badge>
                ) : (
                  canPost && (
                    <>
                      {needsAccount(p.method) && (
                        <input
                          aria-label="Tahsilat hesabı"
                          placeholder="hesap, ör. 499000"
                          className="w-28 rounded border border-[var(--c-line)] px-1 py-0.5 text-xs"
                          value={moneyAccount[p.id] ?? ''}
                          onChange={(e) =>
                            setMoneyAccount({ ...moneyAccount, [p.id]: e.target.value })
                          }
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={post.isPending || (needsAccount(p.method) && !moneyAccount[p.id])}
                        onClick={() => {
                          setError(null);
                          post
                            .mutateAsync({
                              sourceType: 'SALES_PAYMENT',
                              sourceId: p.id,
                              ...(needsAccount(p.method)
                                ? { moneyAccountCode: (moneyAccount[p.id] ?? '').trim() }
                                : {}),
                            })
                            .then(() => payments.refetch())
                            .catch((e: unknown) => setError(errorMessage(e)));
                        }}
                      >
                        Deftere kaydet
                      </Button>
                    </>
                  )
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    setError(null);
                    remove.mutateAsync(p.id).catch((e: unknown) => setError(errorMessage(e)));
                  }}
                >
                  Sil
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotesCard({ invoice }: { invoice: SalesInvoice }) {
  return (
    <Card>
      <CardHeader title="Notlar" />
      <div className="space-y-3 px-4 py-4 text-sm">
        {invoice.notes && (
          <div>
            <p className="text-xs font-semibold text-[var(--c-ink-soft)]">Belgeye basılan not</p>
            <p className="whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}
        {invoice.internalNotes && (
          <div>
            <p className="text-xs font-semibold text-[var(--c-ink-soft)]">
              İç not (basılmaz — düzenlendikten sonra da değiştirilebilir)
            </p>
            <p className="whitespace-pre-wrap">{invoice.internalNotes}</p>
          </div>
        )}
        {invoice.cancelReason && (
          <div>
            <p className="text-xs font-semibold text-[var(--c-ink-soft)]">İptal gerekçesi</p>
            <p className="whitespace-pre-wrap">{invoice.cancelReason}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Belgenin ÜZERİNDE YAZAN değerlerden okunan, bloke ETMEYEN teşhisler.
 *
 * ⚠ Çözümleyicinin kendi uyarıları (`OSS_DESTINATION_RATE_UNVERIFIED`,
 * `FRANCHISE_THRESHOLD_CROSSED`, `VAT_RATE_FORCED_TO_ZERO`…) burada YOKTUR:
 * sunucu onları yalnız günlüğe yazıyor, yanıtta döndürmüyor. Uyarıları
 * panelde göstermenin tek doğru yolu, ucun onları döndürmesidir — kural
 * motorunu panelde ikinci kez yazmak iki tarafın sessizce ayrışması demek olurdu.
 */
function ObservationsCard({ invoice }: { invoice: SalesInvoice }) {
  const observations = documentObservations(invoice);
  if (observations.length === 0) return null;

  return (
    <Card>
      <CardHeader title="Dikkat edilecekler" description="Belgeyi bloke etmez, bilgilendirir." />
      <ul className="space-y-3 px-4 py-4">
        {observations.map((observation, index) => (
          <li key={index} className="flex gap-2 text-sm">
            <Badge tone={observation.tone === 'warn' ? 'warn' : 'neutral'}>
              {observation.tone === 'warn' ? 'uyarı' : 'bilgi'}
            </Badge>
            <span className="flex-1 text-[var(--c-ink-soft)]">{observation.text}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────── modaller

function CancelDraftModal({
  open,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onSubmit: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Taslağı iptal et"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            loading={pending}
            onClick={() => onSubmit(reason.trim() === '' ? null : reason.trim())}
          >
            İptal et
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-[var(--c-ink-soft)]">
        <p>
          ⚠ Bu yalnızca TASLAK iptalidir. Buradaki gerekçe, hiç numara almamış bir taslağın neden
          çöpe atıldığının İÇ NOTUDUR — mali bir belge değildir.
        </p>
        <Input
          label="Gerekçe (isteğe bağlı)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
        />
      </div>
    </Modal>
  );
}

interface CreditLineDraft {
  lineNo: number;
  selected: boolean;
  quantity: string;
  originalQuantity: string;
  description: string;
}

/**
 * Kredi notu kutusu.
 *
 * ⚠ `correctionReason` ZORUNLU: md. 77 §1 uyarınca SEBEPSİZ İADE YOKTUR ve
 * zamanaşımı (md. 82bis) fatura tarihinden değil SEBEBİN tarihinden işler.
 * Sebep kaydedilmezse üç yıl sonra "bu düzeltme hâlâ süresinde miydi" sorusu
 * cevaplanamaz.
 *
 * ⚠ Bu uç bir TASLAK üretir; numarasını kendi serisinden `issue` verir.
 *
 * ⚠ Kutunun içindeki uyarı, ZORUNLU İBARENİN KOŞULUNU anlatır. AR n° 4 md. 4
 * §1, 3° ibareyi Code md. 79 durumlarıyla sınırlıyor ama yazılım koşulu
 * VERİDEN değerlendiremiyor (md. 79'un metni okunmadı) ve ibareyi her kredi
 * notuna basıyor. Operatörün bunu bilmesi gerekir: belgede kendi seçmediği
 * bir cümle görecek.
 */
function CreditNoteModal({
  invoice,
  lines,
  onClose,
  onCreated,
}: {
  invoice: SalesInvoice;
  lines: SalesInvoiceLine[];
  onClose: () => void;
  onCreated: (created: SalesInvoice) => void;
}) {
  const create = useCreateCreditNote();

  const [reason, setReason] = useState<CorrectionReason | ''>('');
  const [issueDate, setIssueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [partial, setPartial] = useState(false);
  const [rows, setRows] = useState<CreditLineDraft[]>(() =>
    lines.map((line) => ({
      lineNo: line.lineNo,
      selected: true,
      quantity: line.quantity,
      originalQuantity: line.quantity,
      description: line.description,
    })),
  );
  const [problem, setProblem] = useState<string | null>(null);

  function patchRow(lineNo: number, patch: Partial<CreditLineDraft>) {
    setRows((current) =>
      current.map((row) => (row.lineNo === lineNo ? { ...row, ...patch } : row)),
    );
  }

  function submit() {
    if (reason === '') {
      setProblem('İade sebebi zorunlu (md. 77 §1).');
      return;
    }

    let lineBodies: CreditNoteLineBody[] | undefined;
    if (partial) {
      const selected = rows.filter((row) => row.selected);
      if (selected.length === 0) {
        setProblem('Kısmi iadede en az bir satır seçilmeli.');
        return;
      }
      const bad = selected.find((row) => !POSITIVE_QUANTITY_PATTERN.test(row.quantity));
      if (bad) {
        setProblem(`Satır ${bad.lineNo}: adet sıfırdan büyük ve en fazla dört ondalık olmalı.`);
        return;
      }
      lineBodies = selected.map((row) => ({ lineNo: row.lineNo, quantity: row.quantity }));
    }

    const body: CreateCreditNoteBody = {
      correctionReason: reason,
      ...(issueDate === '' ? {} : { issueDate }),
      notes: notes.trim() === '' ? null : notes.trim(),
      ...(lineBodies ? { lines: lineBodies } : {}),
    };

    setProblem(null);
    create.mutate(
      { id: invoice.id, body },
      { onSuccess: onCreated, onError: (err) => setProblem(invoiceErrorMessage(err)) },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Kredi notu — ${invoice.documentNumber ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            Kredi notu taslağı aç
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="rounded-[var(--r-md)] bg-[var(--c-warn-soft)] px-3 py-2 text-xs text-[var(--c-warn)]">
          Kredi notuna, AR n° 4 md. 4 §1, 3° uyarınca{' '}
          <span className="font-mono">T.V.A. à reverser à l'Etat…</span> ibaresi otomatik basılır.
          Hüküm ibareyi Code md. 79 durumlarıyla sınırlıyor ama ibare HER kredi notuna konuyor:
          fazladan basmanın maliyeti yoktur (ibare kendi içinde "yalnızca indirim yapılmış olduğu
          ölçüde" der), eksik basmanınki ise KDV'yi geri alma hakkının kendisidir.
        </p>

        <Select
          label="İade sebebi"
          value={reason}
          onChange={(e) => setReason(e.target.value as CorrectionReason)}
          hint="Zamanaşımı (md. 82bis) fatura tarihinden değil SEBEBİN tarihinden işler."
        >
          <option value="">— seçin —</option>
          {CORRECTION_REASONS.map((value) => (
            <option key={value} value={value}>
              {CORRECTION_REASON_LABELS[value]}
            </option>
          ))}
        </Select>

        <Input
          label="Düzenleme tarihi (isteğe bağlı)"
          type="date"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
          hint="Boşsa bugün. Teslim tarihi ORİJİNALİNKİDİR: düzeltme, düzelttiği işlemin olayına bağlanır."
        />

        <div className="rounded-[var(--r-md)] border border-[var(--c-border)] p-3">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--c-brand)]"
              checked={partial}
              onChange={(e) => setPartial(e.target.checked)}
            />
            <span>Kısmi iade (satır ve adet seçerek)</span>
          </label>
          <p className="mt-1 text-xs text-[var(--c-ink-faint)]">
            İşaretlenmezse orijinalin TÜM satırları birebir kopyalanır — tam iade. Belge seviyesi
            indirim de iade edilir, yoksa devletten fazla KDV geri istenirdi.
          </p>

          {partial && (
            <ul className="mt-3 space-y-2">
              {rows.map((row) => (
                <li key={row.lineNo} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-2 h-4 w-4 accent-[var(--c-brand)]"
                    checked={row.selected}
                    onChange={(e) => patchRow(row.lineNo, { selected: e.target.checked })}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs">
                      {row.lineNo}. {row.description}
                    </p>
                    <Input
                      value={row.quantity}
                      inputMode="decimal"
                      disabled={!row.selected}
                      onChange={(e) => patchRow(row.lineNo, { quantity: e.target.value })}
                      hint={`orijinal adet: ${quantity(row.originalQuantity)}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Textarea
          label="Not (belgeye basılır)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {problem && <p className="text-sm text-[var(--c-danger)]">{problem}</p>}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────── küçük parçalar

function Field({
  label,
  value,
  mono,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  hint?: string;
}) {
  return (
    <div className="mt-2">
      <p className="text-xs text-[var(--c-ink-soft)]">{label}</p>
      <p className={mono ? 'font-mono text-[13px]' : undefined}>
        {value && value.length > 0 ? value : <span className="text-[var(--c-ink-faint)]">—</span>}
      </p>
      {hint && <p className="text-xs text-[var(--c-ink-faint)]">{hint}</p>}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-[var(--c-ink-soft)]">{label}</dt>
      <dd className={strong ? 'tabular font-semibold' : 'tabular'}>{value}</dd>
    </div>
  );
}
